/**
 * ChannelsPushedSection — Card mit Feldern die via Channex an OTAs gepusht werden.
 * Sprint-16 Refresh nach Mockup 4e „Pushed to Channels".
 *
 * Enthaelt:
 *   - Identitaet:  Name, PropertyType, Timezone, (Slug im Create-Mode)
 *   - Adresse:     Street, PostalCode, City, CountryCode
 *   - Kapazitaet:  OccupancyAdults, OccupancyChildren (NumberStepper)
 *   - Pricing:     Currency, BaseNightlyRate
 *
 * Icon-Prefix bei Text-Inputs (Home, Clock, MapPin) — matches Mockup 4e.
 */
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { AlertTriangle, Building2, Clock, Globe, Hash, MapPin, Tag } from 'lucide-react';
import { FormField, Input, NumberStepper, Select } from '@/shared/components';
import { propertyTypeOptions } from '@/shared/api';
import { SLEEPING_SPACE_LABELS, SleepingSpacesHint } from '../SleepingSpacesHint';
import type { PropertyValues } from './schema';
import { PropertyAddressAutocomplete } from './PropertyAddressAutocomplete';

interface ChannelsPushedSectionProps {
  register: UseFormRegister<PropertyValues>;
  control: Control<PropertyValues>;
  errors: FieldErrors<PropertyValues>;
  setValue: UseFormSetValue<PropertyValues>;
  watch: UseFormWatch<PropertyValues>;
  slugEditable?: boolean;
  addressEditable?: boolean;
}

export function ChannelsPushedSection({
  register,
  control,
  errors,
  setValue,
  watch,
  slugEditable,
  addressEditable = true,
}: ChannelsPushedSectionProps) {
  const markForReview = () => {
    setValue('latitude', null, { shouldDirty: true });
    setValue('longitude', null, { shouldDirty: true });
    setValue('needsAddressReview', true, { shouldDirty: true });
  };
  const needsAddressReview = watch('needsAddressReview');

  return (
    <div className="bg-card border-line/50 shadow-card rounded-lg border p-6">
      <SectionHeader
        icon={<Globe size={13} strokeWidth={2} />}
        iconTint="navy"
        title="Pushed to Channels"
        subtitle="Sichtbar auf Airbnb & Booking.com — via Channex."
      />

      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="Name" error={errors.name?.message} required>
          <Input
            {...register('name')}
            leftIcon={<Building2 size={14} strokeWidth={2} />}
            placeholder="Loft Mitte"
          />
        </FormField>
        {slugEditable && (
          <FormField
            label="Slug"
            error={errors.slug?.message}
            hint="URL-Handle, eindeutig im Tenant. Nach Anlage fix."
            required
          >
            <Input
              {...register('slug')}
              leftIcon={<Hash size={14} strokeWidth={2} />}
              placeholder="loft-mitte"
            />
          </FormField>
        )}
        <FormField label="Typ" error={errors.propertyType?.message} required>
          <Select {...register('propertyType')}>
            {propertyTypeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField
          label="Zeitzone"
          error={errors.timezone?.message}
          required
          className="md:col-span-2"
        >
          <Select {...register('timezone')} leftIcon={<Clock size={14} strokeWidth={2} />}>
            <option value="Europe/Berlin">Europe/Berlin</option>
            <option value="Europe/Vienna">Europe/Vienna</option>
            <option value="Europe/Zurich">Europe/Zurich</option>
            <option value="Europe/London">Europe/London</option>
            <option value="Europe/Madrid">Europe/Madrid</option>
            <option value="Europe/Rome">Europe/Rome</option>
            <option value="America/New_York">America/New_York</option>
            <option value="UTC">UTC</option>
          </Select>
        </FormField>

        <fieldset disabled={!addressEditable} className="contents">
          <FormField label="Adresse suchen" className="md:col-span-2">
            <Controller
              control={control}
              name="addressQuery"
              render={({ field }) => (
                <PropertyAddressAutocomplete
                  value={field.value ?? ''}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  onSelect={(suggestion) => {
                    const { address } = suggestion;
                    setValue('addressQuery', suggestion.label, { shouldDirty: true });
                    setValue('streetName', address.streetName, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    setValue('houseNumber', address.houseNumber, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    setValue('postalCode', address.postalCode, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    setValue('city', address.city, { shouldDirty: true, shouldValidate: true });
                    setValue('countryCode', address.countryCode, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    setValue('latitude', address.latitude, { shouldDirty: true });
                    setValue('longitude', address.longitude, { shouldDirty: true });
                    setValue('needsAddressReview', suggestion.needsAddressReview, {
                      shouldDirty: true,
                    });
                  }}
                />
              )}
            />
            <p className="text-ink-faint mt-1 text-[11px]">
              Vorhandene Objekte zuerst, danach Adressdaten © OpenStreetMap-Mitwirkende
            </p>
          </FormField>
          <FormField label="Straße" error={errors.streetName?.message} required>
            <Input
              {...register('streetName', { onChange: markForReview })}
              placeholder="Musterstraße"
            />
          </FormField>
          <FormField label="Hausnummer" error={errors.houseNumber?.message} required>
            <Input {...register('houseNumber', { onChange: markForReview })} placeholder="1a" />
          </FormField>
          <FormField label="PLZ" error={errors.postalCode?.message} required>
            <Input {...register('postalCode', { onChange: markForReview })} placeholder="10115" />
          </FormField>
          <FormField label="Stadt" error={errors.city?.message} required>
            <Input
              {...register('city', { onChange: markForReview })}
              leftIcon={<MapPin size={14} strokeWidth={2} />}
              placeholder="Berlin"
            />
          </FormField>
          <FormField label="Land (ISO-2)" error={errors.countryCode?.message} required>
            <Select
              {...register('countryCode', { onChange: markForReview })}
              leftIcon={<Globe size={14} strokeWidth={2} />}
            >
              <option value="DE">DE — Deutschland</option>
              <option value="AT">AT — Österreich</option>
              <option value="CH">CH — Schweiz</option>
              <option value="IT">IT — Italien</option>
              <option value="FR">FR — Frankreich</option>
              <option value="ES">ES — Spanien</option>
              <option value="GB">GB — Vereinigtes Königreich</option>
              <option value="US">US — USA</option>
            </Select>
          </FormField>
          {needsAddressReview && (
            <div
              className="border-sealwax/30 bg-sealwax/5 text-ink flex gap-2 rounded-md border p-3 text-sm md:col-span-2"
              role="status"
            >
              <AlertTriangle className="text-sealwax mt-0.5 shrink-0" size={16} />
              <span>
                Adresse manuell geändert oder unvollständig. Bitte vor der Freigabe prüfen.
              </span>
            </div>
          )}
        </fieldset>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            label={SLEEPING_SPACE_LABELS.adults}
            hint={SLEEPING_SPACE_LABELS.adultsHint}
            error={errors.occupancyAdults?.message}
            required
          >
            <Controller
              control={control}
              name="occupancyAdults"
              render={({ field }) => (
                <NumberStepper
                  value={Number(field.value) || 0}
                  onChange={field.onChange}
                  min={1}
                  max={20}
                />
              )}
            />
          </FormField>
          <FormField
            label={SLEEPING_SPACE_LABELS.children}
            hint={SLEEPING_SPACE_LABELS.childrenHint}
            error={errors.occupancyChildren?.message}
            required
          >
            <Controller
              control={control}
              name="occupancyChildren"
              render={({ field }) => (
                <NumberStepper
                  value={Number(field.value) || 0}
                  onChange={field.onChange}
                  min={0}
                  max={10}
                />
              )}
            />
          </FormField>
        </div>
        <SleepingSpacesHint
          adults={Number(watch('occupancyAdults')) || 0}
          childSpaces={Number(watch('occupancyChildren')) || 0}
        />

        <FormField label="Währung" error={errors.currency?.message} required>
          <Select {...register('currency')}>
            <option value="EUR">EUR — €</option>
            <option value="USD">USD — $</option>
            <option value="GBP">GBP — £</option>
            <option value="CHF">CHF — Fr</option>
          </Select>
        </FormField>
        <FormField
          label="Preis / Nacht (Default)"
          error={errors.baseNightlyRate?.message}
          hint='Default-Rate in Channex. Tagespreise im Tab „Preise & Belegung".'
          required
        >
          <Input
            type="number"
            min={0}
            step="0.01"
            {...register('baseNightlyRate')}
            leftIcon={<Tag size={14} strokeWidth={2} />}
            placeholder="95"
          />
        </FormField>
      </div>
    </div>
  );
}

function SectionHeader({
  icon,
  iconTint,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  iconTint: 'navy' | 'sage' | 'paper';
  title: string;
  subtitle: string;
}) {
  const tintClasses = {
    navy: 'bg-navy/12 text-navy',
    sage: 'bg-sage/15 text-sage',
    paper: 'bg-ink-faint/15 text-ink-soft',
  };
  return (
    <div className="mb-6 flex items-start gap-3">
      <span
        aria-hidden
        className={`grid size-9 shrink-0 place-items-center rounded-md ${tintClasses[iconTint]}`}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-ink font-serif text-[17px] font-medium">{title}</div>
        <p className="text-ink-soft mt-0.5 text-[13px]">{subtitle}</p>
      </div>
    </div>
  );
}

export { SectionHeader as PropertyFormSectionHeader };
